import type { globalHistory } from "@mgdx-libs/router";
import queryString from "query-string";
import { createContext, type PropsWithChildren, useContext, useMemo } from "react";
import {
  type QueryParamAdapter,
  type QueryParamAdapterComponent,
  type QueryParamOptions,
  QueryParamProvider as BaseQueryParamProvider,
} from "use-query-params";

type QueryLocation = Partial<Location> & { search: string };
type History = {
  push(location: QueryLocation): void;
  replace(location: QueryLocation): void;
};
type Props = PropsWithChildren<
  { location: QueryLocation } & (
    | { reachHistory: typeof globalHistory; history?: never }
    | { history: History; reachHistory?: never }
  )
>;

const options: QueryParamOptions = {
  searchStringToObject: queryString.parse,
  objectToSearchString: queryString.stringify,
  // v1 と同じく、同じ値の設定も履歴へ反映し、複数更新をまとめない。
  skipUpdateWhenNoChange: false,
  enableBatching: false,
};

const AdapterContext = createContext<QueryParamAdapter | null>(null);

// adapter を render 内で定義すると、location の変更時に子が再マウントされる。
const Adapter: QueryParamAdapterComponent = ({ children }) => {
  const adapter = useContext(AdapterContext);
  if (!adapter) throw new Error("QueryParamProvider adapter is missing");
  return children(adapter);
};

const createAdapter = ({
  location,
  getLocation,
  navigate,
}: {
  location: QueryLocation;
  getLocation?: () => QueryLocation;
  navigate: (location: QueryLocation, replace: boolean) => void;
}): QueryParamAdapter => {
  let currentLocation = location;
  const update = (nextLocation: QueryLocation, replace: boolean) => {
    // router の再描画前に続けて更新された場合も、直前の変更を引き継ぐ。
    currentLocation = { ...currentLocation, ...nextLocation };
    navigate(currentLocation, replace);
  };
  return {
    get location() {
      // Reach は遷移元の unmount 時にも、遷移先の最新URLを参照する。
      return getLocation?.() ?? currentLocation;
    },
    push: (next) => update(next, false),
    replace: (next) => update(next, true),
  };
};

export const QueryParamProvider = ({ children, location, history, reachHistory }: Props) => {
  const adapter = useMemo(
    () =>
      createAdapter({
        location,
        getLocation: reachHistory ? () => reachHistory.location : undefined,
        navigate: (next, replace) => {
          if (reachHistory) {
            // v1 の Reach 接続と同じく、hash/state は引き継がない。
            void reachHistory.navigate(`${next.protocol}//${next.host}${next.pathname}${next.search}`, { replace });
          } else {
            history[replace ? "replace" : "push"](next);
          }
        },
      }),
    [location, history, reachHistory]
  );

  return (
    <AdapterContext.Provider value={adapter}>
      <BaseQueryParamProvider adapter={Adapter} options={options}>
        {children}
      </BaseQueryParamProvider>
    </AdapterContext.Provider>
  );
};
